import { type VariablesOf } from '@graphql-typed-document-node/core'
import { useAtomValue, useSetAtom } from 'jotai'
import { useResetAtom } from 'jotai/utils'
import React from 'react'

import { brandGroupIdAtom, surveyFilterAtom } from '~/atoms'
import { graphql } from '~/gql'
import { useTabulate } from '~/hooks/useTabulate'
import { exists } from '~/utils'

import {
  commentAtom,
  customerAttributeIdsAtom,
  groupFiltersAtom,
  isPendingTabulationAtom,
  isTabulationReadyAtom,
  lastTabulatedVariablesAtom,
  questionIdsAtom,
  savedCommentAtom,
  tabulationQueryDataAtom,
} from '../atoms'

import { useSurveyId } from './useSurveyId'

const mutationDocument = graphql(`
  mutation CustomizedAnalysisPage_TabulationMutation(
    $surveyId: String!
    $brandGroupId: Int!
    $groupFilters: [GroupFilter!]!
    $questionIds: [String!]!
    $customerAttributeIds: [ID!]!
    $filter: [[CustomerAttributeFilterItemInput!]!]!
  ) {
    count: countCustomizedAnalysis(
      surveyId: $surveyId
      brands9segs: { brandGroupId: $brandGroupId }
      groupFilters: $groupFilters
      answers: { questionIds: $questionIds }
      customerAttributeIds: $customerAttributeIds
      customFilter: { filter: $filter }
    ) {
      awaitId
    }
  }
`)
type MutationVariables = VariablesOf<typeof mutationDocument>

const queryDocument = graphql(`
  query CustomizedAnalysisPage_Tabulation($awaitId: String!) {
    count: countCustomizedAnalysis(awaitId: $awaitId) {
      arrived
      result {
        ...CustomizedAnalysisPage_TabulationQueryData
      }
    }
  }
`)

export const useTabulationQuery = () => {
  const isTabulationReady = useAtomValue(isTabulationReadyAtom)
  const isPendingTabulation = useAtomValue(isPendingTabulationAtom)

  const surveyFilter = useAtomValue(surveyFilterAtom)
  const brandGroupId = useAtomValue(brandGroupIdAtom)
  const surveyId = useSurveyId()
  const groupFilters = useAtomValue(groupFiltersAtom)
  const questionIds = useAtomValue(questionIdsAtom)
  const customerAttributeIds = useAtomValue(customerAttributeIdsAtom)

  const [tabulateState, tabulate] = useTabulate(mutationDocument, queryDocument)

  const setTabulationQueryData = useSetAtom(tabulationQueryDataAtom)
  const setLastTabulatedVariables = useSetAtom(lastTabulatedVariablesAtom)

  const resetComment = useResetAtom(commentAtom)
  const resetSavedComment = useResetAtom(savedCommentAtom)

  const hasTabulatedRef = React.useRef(false)

  const tabulateIfNeeded = React.useCallback(async () => {
    if (!isTabulationReady || isPendingTabulation || !exists(brandGroupId)) {
      return
    }

    const variables: MutationVariables = {
      brandGroupId,
      surveyId,
      groupFilters,
      questionIds,
      customerAttributeIds,
      filter: surveyFilter?.filterGroups ?? [],
    }
    const queryData = await tabulate(variables)

    setTabulationQueryData(queryData)
    setLastTabulatedVariables(variables)

    if (hasTabulatedRef.current) {
      resetComment()
      resetSavedComment()
    }
    hasTabulatedRef.current = true
  }, [
    brandGroupId,
    customerAttributeIds,
    groupFilters,
    isPendingTabulation,
    isTabulationReady,
    questionIds,
    resetComment,
    resetSavedComment,
    setLastTabulatedVariables,
    setTabulationQueryData,
    surveyFilter?.filterGroups,
    surveyId,
    tabulate,
  ])

  React.useEffect(() => {
    tabulateIfNeeded()
  }, [tabulateIfNeeded])

  return tabulateState.loading
}
